"use client";

import { LazyMotion, domAnimation, m } from "framer-motion";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { cn } from "@/lib/utils";
import { Cpu, Database } from "lucide-react";
import { formatFileSize } from "@/lib/developer-utils";
import { formatExactDate } from "@/lib/app-detail-utils";
import { countryCodeToFlagEmoji, getCountryName } from "@/lib/countries";

interface AppInformationProps {
  provider: string;
  developerUrl?: string | null;
  size?: string | number | null;
  category?: string | null;
  compatibility?: string | null;
  languages?: string[] | null;
  ageRating?: string | null;
  copyright?: string | null;
  // Release details: the newest version and its date, the App Store release date (and the storefront's own when it differs), the publisher's country.
  version?: string | null;
  updatedAt?: string | null;
  releaseDate?: string | null;
  countryReleaseDate?: string | null;
  countryCode?: string | null;
  publisherCountry?: string | null;
  isLoading?: boolean;
}

// Terminal label mapping for Bloomberg-style display
const TERMINAL_LABELS: Record<string, string> = {
  "Provider": "DEV.NAME",
  "Size": "SIZE.MB",
  "Category": "CAT.ID",
  "Compatibility": "IOS.MIN",
  "Languages": "LANG.SUPPORT",
  "Age Rating": "AGE.RATING",
  "Version": "VER.CURRENT",
  "Updated": "VER.DATE",
  "Released": "REL.DATE",
  "Publisher country": "PUB.COUNTRY",
};

const formatLanguages = (langs: string[] | null | undefined) => {
  if (!langs || langs.length === 0) return null;
  if (langs.length <= 3) return langs.join(", ");
  return `${langs.slice(0, 3).join(", ")} +${langs.length - 3}`;
};

export function AppInformation({
  provider,
  developerUrl,
  size,
  category,
  compatibility,
  languages,
  ageRating,
  copyright,
  version,
  updatedAt,
  releaseDate,
  countryReleaseDate,
  countryCode,
  publisherCountry,
  isLoading = false,
}: AppInformationProps) {
  const { isTerminal, hasGlow, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;

  // size_bytes arrives as a number from the table and as a string from iTunes; both format the same way.
  const formattedSize = formatFileSize(size ?? undefined);
  // "May 18, 2023 (US: May 30, 2023)" when the storefront's own release date differs from the App Store one.
  const released = formatExactDate(releaseDate, { utc: true });
  const releasedLocally = formatExactDate(countryReleaseDate, { utc: true });
  const releasedValue = !released
    ? releasedLocally || null
    : releasedLocally && releasedLocally !== released && countryCode
      ? `${released} (${countryCode.toUpperCase()}: ${releasedLocally})`
      : released;
  // Sensor Tower writes a code for most publishers ("US") and a name for some ("Switzerland").
  const publisherValue = !publisherCountry
    ? null
    : /^[A-Za-z]{2}$/.test(publisherCountry)
      ? `${countryCodeToFlagEmoji(publisherCountry)} ${getCountryName(publisherCountry) ?? publisherCountry.toUpperCase()}`
      : publisherCountry;

  const items = [
    { label: "Provider", value: provider, link: developerUrl },
    { label: "Version", value: version || null },
    { label: "Updated", value: formatExactDate(updatedAt, { utc: true }) || null },
    { label: "Released", value: releasedValue },
    { label: "Publisher country", value: publisherValue },
    { label: "Size", value: formattedSize },
    { label: "Category", value: category },
    { label: "Compatibility", value: compatibility ? `iOS ${compatibility}+` : null },
    { label: "Languages", value: formatLanguages(languages) },
    { label: "Age Rating", value: ageRating },
  ].filter(item => item.value);

  // Terminal mode section header
  const terminalHeader = showTerminal ? (
    <div className="flex items-center gap-2 mb-3">
      <Database className="h-3.5 w-3.5 text-emerald-500" />
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 [text-shadow:0_0_12px_rgba(52,211,153,0.7)]">
        TECH.SPECS
      </span>
      <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/50 to-transparent" />
      <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-600">
        LIVE
      </span>
    </div>
  ) : null;

  // Terminal skeleton
  const terminalSkeleton = (
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn(
        "bg-black border-2 border-emerald-700/50 p-4 rounded-none relative overflow-hidden",
        "shadow-[0_0_15px_rgba(16,185,129,0.15),inset_0_0_20px_rgba(16,185,129,0.05)]"
      )}
    >
      {/* Scanline overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(16,185,129,0.1)_2px,rgba(16,185,129,0.1)_4px)]" />

      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-emerald-400/60" />
      <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-emerald-400/60" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-emerald-400/60" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-emerald-400/60" />

      <div className="space-y-3 relative z-10">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex justify-between items-center py-2 border-b border-emerald-800/30 last:border-0">
            <div className="h-3 w-20 bg-emerald-950/80 border border-emerald-800/30 animate-pulse" />
            <div className="h-3 w-32 bg-emerald-950/80 border border-emerald-800/30 animate-pulse" />
          </div>
        ))}
      </div>
    </m.div>
  );

  if (isLoading) {
    if (showTerminal) {
      return (
        <LazyMotion features={domAnimation}>
        <div className="h-full">
          {terminalHeader}
          {terminalSkeleton}
        </div>
        </LazyMotion>
      );
    }

    return (
      <div className="h-full">
        <h2 className="text-xl font-bold tracking-tight mb-3">Information</h2>
        <div className="divide-y divide-border/40">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex justify-between items-center py-3 first:pt-0">
              <div className="h-4 w-20 bg-muted/50 rounded animate-pulse" />
              <div className="h-4 w-32 bg-muted/50 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Terminal mode rendering
  if (showTerminal) {
    return (
      <LazyMotion features={domAnimation}>
      <div className="h-full">
        {terminalHeader}
        <m.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className={cn(
            "bg-black border-2 border-emerald-700/50 p-4 rounded-none relative overflow-hidden",
            "shadow-[0_0_15px_rgba(16,185,129,0.15),inset_0_0_20px_rgba(16,185,129,0.05)]",
            hasGlow && "animate-terminal-border-pulse"
          )}
        >
          {/* Scanline overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-10 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(16,185,129,0.1)_2px,rgba(16,185,129,0.1)_4px)]" />

          {/* Corner accents */}
          <div
            className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-emerald-400 opacity-80"
            style={{ boxShadow: "0 0 7px rgba(52,211,153,0.5)" }}
          />
          <div
            className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-emerald-400 opacity-80"
            style={{ boxShadow: "0 0 7px rgba(52,211,153,0.5)" }}
          />
          <div
            className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-emerald-400 opacity-80"
            style={{ boxShadow: "0 0 7px rgba(52,211,153,0.5)" }}
          />
          <div
            className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-emerald-400 opacity-80"
            style={{ boxShadow: "0 0 7px rgba(52,211,153,0.5)" }}
          />

          {/* Status indicator */}
          <div className="absolute top-2 right-2 flex items-center gap-1.5">
            <div
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              style={{ boxShadow: "0 0 6px rgba(52,211,153,0.9)" }}
            />
            <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-500">DATA</span>
          </div>

          {/* Data grid */}
          <div className="relative z-10 space-y-0">
            {items.map((item, index) => (
              <m.div
                key={item.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start justify-between py-2.5 border-b border-emerald-800/40 last:border-0"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-600 shrink-0 [text-shadow:0_0_4px_rgba(52,211,153,0.3)]">
                  {TERMINAL_LABELS[item.label] || item.label.toUpperCase().replace(" ", ".")}
                </span>
                {item.link ? (
                  <m.a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "font-mono text-[12px] text-emerald-300 text-right ml-4 max-w-[60%] truncate",
                      "hover:text-emerald-200 transition-colors",
                      "[text-shadow:0_0_8px_rgba(52,211,153,0.6)]"
                    )}
                    whileHover={{
                      textShadow: "0 0 12px rgba(52,211,153,1)",
                    }}
                  >
                    <span className="text-emerald-500">[</span>
                    {item.value}
                    <span className="text-emerald-500">]</span>
                    <span className="text-emerald-500 ml-1">→</span>
                  </m.a>
                ) : (
                  <span className="font-mono text-[12px] text-emerald-300 text-right ml-4 max-w-[60%] truncate [text-shadow:0_0_8px_rgba(52,211,153,0.6)]">
                    {item.value}
                  </span>
                )}
              </m.div>
            ))}
          </div>

          {/* Footer status bar */}
          <div className="relative z-10 mt-3 pt-2 border-t border-emerald-800/40 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Cpu className="h-3 w-3 text-emerald-600/70" />
              <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-600/70">
                {items.length} FIELDS
              </span>
            </div>
            <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-500">
              SYS.OK
            </span>
          </div>
        </m.div>

        {copyright && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-3 pt-2 font-mono text-[9px] text-emerald-700/50 uppercase tracking-wider border-t border-emerald-800/20"
          >
            <span className="text-emerald-600/50">{'//'}</span> {copyright}
          </m.div>
        )}
      </div>
      </LazyMotion>
    );
  }

  // Default light/dark mode rendering - unchanged
  return (
    <div className="h-full">
      <h2 className="text-xl font-bold tracking-tight mb-3">Information</h2>
      <div className="divide-y divide-border/40">
        {items.map((item) => (
          <div key={item.label} className="flex items-start justify-between py-3 first:pt-0">
            <span className="text-[15px] text-muted-foreground shrink-0">
              {item.label}
            </span>
            {item.link ? (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-[15px] font-medium hover:underline text-right ml-4 max-w-[60%] ${showTerminal ? "text-emerald-400 hover:text-emerald-300" : "text-blue-500"}`}
              >
                {item.value}
              </a>
            ) : (
              <span className="text-[15px] font-medium text-foreground text-right ml-4 max-w-[60%]">
                {item.value}
              </span>
            )}
          </div>
        ))}
      </div>
      {copyright && (
        <div className="pt-4 mt-2 text-xs text-muted-foreground/50 border-t border-border/20">
          {copyright}
        </div>
      )}
    </div>
  );
}
